// Phase 2: Data Freshness Alert Component
// Shows real-time status of Meta/Spotify/HypeDdit data

import React from 'react';
import { AlertCircle, CheckCircle, XCircle, ExternalLink } from 'lucide-react';
import type { DataFreshnessStatus } from '../../../shared/types/reports';

interface DataFreshnessAlertProps {
  meta?: DataFreshnessStatus;
  spotify?: DataFreshnessStatus;
  hypeddit?: DataFreshnessStatus;
}

export const DataFreshnessAlert: React.FC<DataFreshnessAlertProps> = ({
  meta,
  spotify,
  hypeddit,
}) => {
  const hasStaleData = 
    meta?.status === 'stale' || 
    spotify?.status === 'stale' || 
    hypeddit?.status === 'stale';

  const hasMissingData = 
    meta?.status === 'missing' || 
    spotify?.status === 'missing';

  if (!hasStaleData && !hasMissingData) {
    // All data is fresh
    return (
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-green-900 dark:text-green-100">
              All Data Fresh
            </h3>
            <div className="mt-1 text-sm text-green-700 dark:text-green-300 space-y-0.5">
              {meta && meta.status === 'fresh' && (
                <div>• Meta Ads: Updated {meta.ageMinutes}m ago (via Monthly Budgets)</div>
              )}
              {spotify && spotify.status === 'fresh' && (
                <div>• Spotify: Updated {spotify.ageMinutes}m ago</div>
              )}
              {hypeddit && hypeddit.status === 'fresh' && (
                <div>• HypeDdit: Updated {hypeddit.ageMinutes}m ago</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`border rounded-lg p-4 ${
      hasMissingData 
        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
        : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
    }`}>
      <div className="flex items-start gap-3">
        {hasMissingData ? (
          <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
        ) : (
          <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
        )}
        <div className="flex-1">
          <h3 className={`text-sm font-medium ${
            hasMissingData 
              ? 'text-red-900 dark:text-red-100' 
              : 'text-amber-900 dark:text-amber-100'
          }`}>
            {hasMissingData ? 'Data Refresh Required' : 'Data Status'}
          </h3>
          <div className={`mt-1 text-sm space-y-1 ${
            hasMissingData 
              ? 'text-red-700 dark:text-red-300' 
              : 'text-amber-700 dark:text-amber-300'
          }`}>
            {meta && (
              <div className="flex items-center justify-between">
                <span>
                  • Meta Ads: {meta.status === 'missing' 
                    ? 'No data available' 
                    : meta.status === 'stale' 
                    ? `Last updated ${meta.ageDays}d ago` 
                    : `Fresh (${meta.ageMinutes}m ago)`}
                </span>
                {(meta.status === 'stale' || meta.status === 'missing') && (
                  <a
                    href="/budget"
                    className="inline-flex items-center gap-1 text-xs underline hover:no-underline"
                  >
                    Refresh <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            )}
            {spotify && (
              <div className="flex items-center justify-between">
                <span>
                  • Spotify: {spotify.status === 'missing' 
                    ? 'No data available' 
                    : spotify.status === 'stale' 
                    ? `Last upload ${spotify.ageDays}d ago` 
                    : `Fresh (${spotify.ageMinutes}m ago)`}
                </span>
                {(spotify.status === 'stale' || spotify.status === 'missing') && (
                  <a
                    href="/datauploads"
                    className="inline-flex items-center gap-1 text-xs underline hover:no-underline"
                  >
                    Upload Data <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            )}
            {hypeddit && hypeddit.status !== 'missing' && (
              <div className="flex items-center justify-between">
                <span>
                  • HypeDdit: {hypeddit.status === 'stale' 
                    ? `Last upload ${hypeddit.ageDays}d ago` 
                    : `Fresh (${hypeddit.ageMinutes}m ago)`}
                </span>
                {hypeddit.status === 'stale' && (
                  <a
                    href="/datauploads"
                    className="inline-flex items-center gap-1 text-xs underline hover:no-underline"
                  >
                    Upload Data <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            )}
          </div>
          {hasMissingData && (
            <p className="mt-2 text-xs text-red-600 dark:text-red-400">
              ⚠️ Cannot generate report without fresh data. Please refresh data sources above.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
